import { defineStore } from "pinia";
import { ref } from "vue";

export interface Notification {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
}

export const useUIStore = defineStore("ui", () => {
  // State
  const notifications = ref<Notification[]>([]);
  const globalLoading = ref(false);
  const sidebarOpen = ref(false);
  const theme = ref<"light" | "dark">("light");

  // Actions
  const addNotification = (notification: Omit<Notification, "id">) => {
    const id = Date.now().toString();
    const newNotification: Notification = {
      id,
      duration: 5000,
      ...notification,
    };

    notifications.value.push(newNotification);

    // Auto remove after duration
    if (!newNotification.persistent && newNotification.duration) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  };

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  const clearNotifications = () => {
    notifications.value = [];
  };

  const setGlobalLoading = (loading: boolean) => {
    globalLoading.value = loading;
  };

  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
  };

  const setSidebarOpen = (open: boolean) => {
    sidebarOpen.value = open;
  };

  const setTheme = (newTheme: "light" | "dark") => {
    theme.value = newTheme;
    if (typeof window !== 'undefined') {
      document.documentElement.classList.toggle("dark", newTheme === "dark");
      localStorage.setItem("theme", newTheme);
    }
  };

  const initTheme = () => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem("theme") as
        | "light"
        | "dark"
        | null;
      if (savedTheme) {
        setTheme(savedTheme);
      } else {
        // Check system preference
        const prefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)",
        ).matches;
        setTheme(prefersDark ? "dark" : "light");
      }
    }
  };

  // Initialize theme on client side
  if (typeof window !== 'undefined') {
    initTheme();
  }

  return {
    // State
    notifications,
    globalLoading,
    sidebarOpen,
    theme,

    // Actions
    addNotification,
    removeNotification,
    clearNotifications,
    setGlobalLoading,
    toggleSidebar,
    setSidebarOpen,
    setTheme,
    initTheme,
  };
});
